import React, { useState, useEffect, useRef } from 'react';
import { Input } from './input';
import { Label } from './label';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ValidationResult {
  available: boolean;
  reason: string | null;
}

interface ValidatedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  validationEndpoint?: string;
  debounceMs?: number;
  onValidationChange?: (isValid: boolean, message?: string) => void;
}

export function ValidatedInput({
  label,
  validationEndpoint,
  debounceMs = 500,
  onValidationChange,
  className,
  ...props
}: ValidatedInputProps) {
  const [value, setValue] = useState('');
  const [validationState, setValidationState] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [validationMessage, setValidationMessage] = useState<string>('');
  
  // Use ref to store the callback so we don't have to include it in dependencies
  const onValidationChangeRef = useRef(onValidationChange);
  
  // Update ref when callback changes
  React.useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  useEffect(() => {
    if (!validationEndpoint || !value || value.length < 2) {
      setValidationState('idle');
      setValidationMessage('');
      onValidationChangeRef.current?.(true);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setValidationState('validating');
      
      try {
        // Get auth token for API calls
        const token = localStorage.getItem('supabase.auth.token') || sessionStorage.getItem('supabase.auth.token');
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        
        if (token) {
          try {
            const authData = JSON.parse(token);
            if (authData?.access_token) {
              headers['Authorization'] = `Bearer ${authData.access_token}`;
            }
          } catch (e) {
            // Token parsing failed, continue without auth
          }
        }
        
        const response = await fetch(`${validationEndpoint}/${encodeURIComponent(value)}`, {
          headers
        });
        const result: ValidationResult = await response.json();
        
        if (result.available) {
          setValidationState('valid');
          setValidationMessage('Available');
          onValidationChangeRef.current?.(true);
        } else {
          setValidationState('invalid');
          setValidationMessage(result.reason || 'Not available');
          onValidationChangeRef.current?.(false, result.reason || 'Not available');
        }
      } catch (error) {
        setValidationState('invalid');
        setValidationMessage('Unable to validate');
        onValidationChangeRef.current?.(false, 'Unable to validate');
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [value, validationEndpoint, debounceMs]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    props.onChange?.(e);
  };

  const getValidationIcon = () => {
    switch (validationState) {
      case 'validating':
        return <Loader2 className="h-4 w-4 animate-spin text-gray-400" />;
      case 'valid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'invalid':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getValidationColor = () => {
    switch (validationState) {
      case 'valid':
        return 'border-green-500 focus:border-green-500';
      case 'invalid':
        return 'border-red-500 focus:border-red-500';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-1">
      <Label htmlFor={props.id}>{label}</Label>
      <div className="relative">
        <Input
          {...props}
          value={value}
          onChange={handleChange}
          className={cn(getValidationColor(), className)}
        />
        {validationEndpoint && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {getValidationIcon()}
          </div>
        )}
      </div>
      {validationMessage && (
        <p className={cn(
          "text-sm",
          validationState === 'valid' ? "text-green-600" : "text-red-600"
        )}>
          {validationMessage}
        </p>
      )}
    </div>
  );
}
